import React, { useMemo } from 'react';
// Import required Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

/**
 * DashboardCharts
 * ---------------
 * Shows:
 *  - Age group distribution (bar)
 *  - Gender distribution (pie)
 *  - Visit trend over months (line)
 *  - Visit frequency per patient (bar)
 *
 * Props:
 *  - patients: array of patient objects with fields like:
 *    - age, gender, visits (array with visit_date)
 */
const DashboardCharts = ({ patients }) => {
  // Ensure patients is ALWAYS an array - bulletproof defense
  const safePatients = Array.isArray(patients)
    ? patients
    : patients && typeof patients === 'object'
      ? [patients]   // if a single patient object accidentally passed
      : [];          // fallback to empty array

  // Color palette that matches your branding (blue / red / teal / amber)
  const COLORS = {
    blue: '#004AAD',
    red: '#E31E24',
    teal: '#0d9488',
    amber: '#f59e0b',
    gray: '#94a3b8',
  };

  /**
   * Helper: parse date string safely
   * Returns null if invalid date
   */
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  /**
   * MEMOIZED DATA TRANSFORMATIONS
   * Use useMemo so we don't recompute on every render unnecessarily
   */
  const {
    ageGroupData,
    genderData,
    visitTrendData,
    visitFrequencyData,
  } = useMemo(() => {
    // Age groups buckets
    const ageBuckets = {
      '0-12': 0,
      '13-19': 0,
      '20-35': 0,
      '36-50': 0,
      '51-65': 0,
      '66+': 0,
      Unknown: 0,
    };

    // Gender buckets
    const genderBuckets = {
      Male: 0,
      Female: 0,
      Other: 0,
      Unknown: 0,
    };

    // Visits per month (last 6 months)
    const visitBuckets = {}; // key: 'YYYY-MM' => count

    // Visit frequency per patient (how many visits each has)
    const visitFrequencyBuckets = {
      '1': 0,
      '2-3': 0,
      '4-6': 0,
      '7+': 0,
    };

    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);

    // Loop through all patients once
    safePatients.forEach((patient) => {
      // Safety check: ensure patient is an object
      if (!patient || typeof patient !== 'object') return;

      // --------- Age Group ---------
      const age = Number(patient.age);
      if (!isNaN(age)) {
        if (age <= 12) ageBuckets['0-12']++;
        else if (age <= 19) ageBuckets['13-19']++;
        else if (age <= 35) ageBuckets['20-35']++;
        else if (age <= 50) ageBuckets['36-50']++;
        else if (age <= 65) ageBuckets['51-65']++;
        else ageBuckets['66+']++;
      } else {
        ageBuckets.Unknown++;
      }

      // --------- Gender ---------
      const genderRaw = (patient.gender || patient.sex || '').toString().toLowerCase();
      if (genderRaw.startsWith('m')) genderBuckets.Male++;
      else if (genderRaw.startsWith('f')) genderBuckets.Female++;
      else if (genderRaw) genderBuckets.Other++;
      else genderBuckets.Unknown++;

      // --------- Visits / Trends / Frequency ---------
      const visits = Array.isArray(patient.visits) ? patient.visits : [];
      const visitCount = visits.length;

      // Visit frequency bucket
      if (visitCount === 1) visitFrequencyBuckets['1']++;
      else if (visitCount >= 2 && visitCount <= 3) visitFrequencyBuckets['2-3']++;
      else if (visitCount >= 4 && visitCount <= 6) visitFrequencyBuckets['4-6']++;
      else if (visitCount >= 7) visitFrequencyBuckets['7+']++;

      // Visit trend (monthly)
      visits.forEach((visit) => {
        // Safety check for visit object
        if (!visit || typeof visit !== 'object') return;

        const d = parseDate(visit.visit_date || visit.date);
        if (!d) return;

        // Only consider last 6 months
        if (d < sixMonthsAgo) return;

        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; // "YYYY-MM"
        visitBuckets[key] = (visitBuckets[key] || 0) + 1;
      });
    });

    // Transform ageBuckets into array for Recharts
    const ageGroupData = Object.entries(ageBuckets).map(([group, count]) => ({
      group,
      count,
    }));

    // Transform genderBuckets into array
    const genderData = Object.entries(genderBuckets)
      .map(([name, value]) => ({
        name,
        value,
      }))
      .filter(item => item.value > 0); // Only show genders with data

    // Prepare visit trend data sorted by month (last 6 months)
    const visitTrendData = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

      visitTrendData.push({
        month: d.toLocaleString('en-US', { month: 'short' }),
        count: visitBuckets[key] || 0,
      });
    }

    // Visit frequency array
    const visitFrequencyData = Object.entries(visitFrequencyBuckets).map(([range, count]) => ({
      range,
      count,
    }));

    return {
      ageGroupData,
      genderData,
      visitTrendData,
      visitFrequencyData,
    };
  }, [safePatients]); // Dependency on safePatients

  // If no data available, show empty state
  if (safePatients.length === 0) {
    return (
      <div className="card bg-slate-50 border-2 border-dashed border-slate-300">
        <div className="text-center py-12">
          <div className="text-slate-400 mb-3">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-display font-bold text-slate-700 mb-2">
            No Data Available
          </h3>
          <p className="text-sm text-slate-500 font-sans">
            Statistics will appear here once you have patient data
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Row 1: Visit Trend (line) */}
      <div className="card">
        <h3 className="text-lg font-display font-bold text-slate-900 mb-4">
          Visit Trend (Last 6 Months)
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={visitTrendData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={{ stroke: '#cbd5e1' }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: '#64748b' }}
                axisLine={{ stroke: '#cbd5e1' }}
              />
              <Tooltip
                contentStyle={{ 
                  borderRadius: '0.75rem', 
                  borderColor: '#e2e8f0', 
                  fontSize: 12,
                  fontFamily: 'Titillium Web, sans-serif'
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={COLORS.blue}
                strokeWidth={2.5}
                dot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: COLORS.blue }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2: Age Groups + Gender + Visit Frequency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Age Group Distribution */}
        <div className="card">
          <h3 className="text-lg font-display font-bold text-slate-900 mb-4">
            Patient Age Groups
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageGroupData} margin={{ top: 10, right: 10, left: -15, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="group"
                  tick={{ fontSize: 11, fill: '#64748b', fontFamily: 'Titillium Web, sans-serif' }}
                  angle={-20}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#64748b' }}
                />
                <Tooltip
                  contentStyle={{ 
                    borderRadius: '0.75rem', 
                    borderColor: '#e2e8f0', 
                    fontSize: 12,
                    fontFamily: 'Titillium Web, sans-serif'
                  }}
                />
                <Bar dataKey="count" fill={COLORS.teal} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Gender Distribution + Visit Frequency stacked vertically */}
        <div className="space-y-4">
          {/* Gender Distribution (Pie) */}
          <div className="card">
            <h3 className="text-sm font-display font-bold text-slate-900 mb-3">
              Gender Distribution
            </h3>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={genderData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    labelLine={false}
                    label={({ name, percent }) =>
                      percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''
                    }
                    style={{ fontFamily: 'Titillium Web, sans-serif', fontSize: '11px' }}
                  >
                    {genderData.map((entry, index) => {
                      const colorMap = [COLORS.blue, COLORS.red, COLORS.teal, COLORS.gray];
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={colorMap[index % colorMap.length]}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    contentStyle={{ 
                      borderRadius: '0.75rem', 
                      borderColor: '#e2e8f0', 
                      fontSize: 12,
                      fontFamily: 'Titillium Web, sans-serif'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={24} 
                    iconSize={8}
                    wrapperStyle={{ fontFamily: 'Titillium Web, sans-serif', fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Visit Frequency */}
          <div className="card">
            <h3 className="text-sm font-display font-bold text-slate-900 mb-3">
              Visit Frequency (per Patient)
            </h3>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={visitFrequencyData}
                  margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="range"
                    tick={{ fontSize: 11, fill: '#64748b', fontFamily: 'Titillium Web, sans-serif' }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                  />
                  <Tooltip
                    contentStyle={{ 
                      borderRadius: '0.75rem', 
                      borderColor: '#e2e8f0', 
                      fontSize: 12,
                      fontFamily: 'Titillium Web, sans-serif'
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill={COLORS.amber}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;